import { ErrorHandler, Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { saveError } from './error.actions';
import { Store } from '@ngrx/store';
import { LogService } from '../log/log.service';
import { saveLogs, saveLogsByDate } from '../../domain/log/log.actions';
import { LogLevel } from '../../domain/log/log.model';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class DottnetErrorHandler extends ErrorHandler {
	constructor(
		private injector: Injector,
	) {
		super();
	}

	handleError(error: Error) {

		console.log('Error handler activated', error);

		// here we inject services because the errorHandler is created before providers, and importing it the standard way failed with a circular dependency injection
		// https://stackoverflow.com/questions/41585863/injecting-services-in-custom-errorhandler-causes-cannot-instantiate-cyclic-depe

		const platformId = this.injector.get(PLATFORM_ID);
		
		const store = this.injector.get(Store);
		const logService = this.injector.get(LogService);

		logService.info('Unhandled error', error.message);
		const logsStartingDate: Date = new Date();

		// minutes to subtract in milliseconds
		const minutesToSubtract: number = 20 * 60000;
		// To unix time
		const startingDate = new Date(logsStartingDate.getTime() - minutesToSubtract);

		// if error is 'google is not defined' and ADS BLock is active, it means that adBlock blocks download of js files
		// and ads functions can't  work properly. We don't send logs to client
		if (
			error?.message.toLowerCase().includes('google is not defined') &&
			globalThis.adsVar === 'ADSBLOCK'
		) {
			logService.info('Google is not defined because of AD block active. Not sending logs to client');
		} else {
			if (isPlatformBrowser(platformId)) {

				store.dispatch(saveLogsByDate({ startingDate: startingDate }));

				store.dispatch(saveError({ error }));
			} else {
				logService.info('Error handler not saving logs serverside');		
			}
		}

		// A new app version was deployed while this tab still holds an old index:
		// it references chunk hashes that no longer exist, so a lazy import throws.
		// Match the real native-ESM messages (NOT the old Webpack "ChunkLoadError"),
		// then reload to pull the fresh index — nginx now serves the HTML no-cache,
		// so the reload gets the new hashes. A short-window sessionStorage guard
		// stops a reload loop if the fresh version still fails (genuine 404/offline)
		// while still allowing a legitimate reload after a later deploy.
		const chunkMsg = (error?.message ?? '').toLowerCase();
		const isChunkLoadError =
			chunkMsg.includes('failed to fetch dynamically imported module') ||
			chunkMsg.includes('importing a module script failed') ||
			chunkMsg.includes('error loading dynamically imported module') ||
			chunkMsg.includes('chunkloaderror');

		if (isChunkLoadError && isPlatformBrowser(platformId)) {
			const RELOAD_AT = 'chunk-reload-at';
			// Storage can be unavailable (private mode / disabled) or throw on
			// setItem — the error handler must never throw while handling an error.
			let lastReload = 0;
			try {
				lastReload = Number(sessionStorage.getItem(RELOAD_AT)) || 0;
			} catch {
				// no storage — treat as no prior reload
			}
			if (Date.now() - lastReload > 20000) {
				try {
					sessionStorage.setItem(RELOAD_AT, String(Date.now()));
				} catch {
					// best effort — reload anyway
				}
				logService.info('ChunkLoadError: reloading once for the new app version');
				location.reload();
				return;
			}
			logService.info('ChunkLoadError persisted right after a reload — not looping');
		}

		super.handleError(error);
	}
}
